/**
 * Pure deep-link parsing shared by `useStartParamRedirect` (Telegram
 * `start_param`, DUE-156) and the iOS `appUrlOpen` listener
 * (`lib/platform/ios.ts`, subproject D task 4 — see
 * `docs/superpowers/specs/2026-07-13-ios-apns-deeplinks-design.md` §3).
 *
 * No IO, no React, no Capacitor — safe to import from anywhere (hooks,
 * platform adapters, tests) without pulling in a runtime.
 */

export const SHARE_PREFIX = "share_";
export const REF_PREFIX = "ref_";

/**
 * Resolves a Telegram/deep-link `start_param` value to an in-app route, or
 * `null` when the param doesn't map to one. Mirrors
 * `useStartParamRedirect`'s original inline parsing exactly:
 *   - `share_<hash>` -> `/s/<hash>` (no length check on `<hash>` — an empty
 *     hash resolves to `/s/`, same as the original hook code).
 *   - `ref_<code>` -> `null` (referral codes are handled elsewhere, e.g.
 *     during onboarding — not a route).
 *   - anything else -> `null` (unknown prefix).
 */
export function resolveStartParam(param: string): string | null {
  if (param.startsWith(SHARE_PREFIX)) {
    return `/s/${param.slice(SHARE_PREFIX.length)}`;
  }
  return null;
}

// Top-level path segments the app actually serves (mirrors `app/routes.tsx`
// at the level deep links care about). Kept as an explicit allow-list —
// not derived from the router — so an unrecognized `duetto://` host or
// `https://app.theduetto.com` path never blindly navigates the app
// somewhere unintended; the catch-all "*" route sends anything else to
// /error anyway, but deep links should fail closed (no navigation) rather
// than bounce through an error screen.
const KNOWN_ROUTE_SEGMENTS = new Set([
  // Tab roots (DUE-384 — were missing, so tab deep links were dropped).
  "today",
  "friends",
  "you",
  "home",
  "history",
  "synastry",
  "transits",
  "readings",
  "mood",
  "archetype",
  "partners",
  "paywall",
  "subscription",
  "profile",
  "support",
  "account-deleted",
  "s",
  "admin",
  "onboarding",
]);

function isKnownAppRoute(pathname: string): boolean {
  const segment = pathname.split("/").filter(Boolean)[0];
  return segment !== undefined && KNOWN_ROUTE_SEGMENTS.has(segment);
}

const DEEPLINK_SCHEME = "duetto:";
// DUE-318: `psyna.app` is the browser surface's own origin, and — once
// `share_links_target_web` flips the share-link generator over — the host
// share URLs are minted against. Until then it's simply another universal
// link host: an `https://psyna.app/...` link resolves exactly like
// `app.theduetto.com` does today, so a share URL made under either host
// behaves identically whether opened in the iOS app or the browser.
const UNIVERSAL_LINK_HOSTS: ReadonlySet<string> = new Set([
  "app.psyna.app",
  "psyna.app",
  // Legacy host, 301s to psyna.app after DUE-265; links minted before the
  // move and older iOS builds still carry it.
  "app.theduetto.com",
]);

/**
 * Resolves a deep-link URL — either the custom `duetto://` scheme (push
 * payloads, `simctl openurl`) or an `https://app.theduetto.com/...` /
 * `https://psyna.app/...` link (share URLs, opened in-app once Universal
 * Links land in subproject E) — to an in-app route, or `null` if it
 * doesn't resolve to one.
 *
 * `duetto://transits/2026-07-13` parses as host=`transits`,
 * pathname=`/2026-07-13` (WHATWG URL treats the segment right after `//` as
 * the authority for any scheme) — recombined into `/transits/2026-07-13`.
 *
 * A `?start=<param>` query param (either scheme) takes priority and is
 * resolved via `resolveStartParam` — matching Telegram's `start_param`
 * handling so share links behave identically across platforms. Falling
 * back to path-based routing only happens when `start` is absent.
 */
export function resolveDeepLinkUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  let pathname: string;
  if (parsed.protocol === DEEPLINK_SCHEME) {
    pathname = `/${parsed.host}${parsed.pathname}`;
  } else if (parsed.protocol === "https:" && UNIVERSAL_LINK_HOSTS.has(parsed.host)) {
    pathname = parsed.pathname;
  } else {
    // Unknown scheme/host — never reachable in practice (iOS only invokes
    // this listener for URLs matching the registered scheme/associated
    // domain), but fail closed for a pure function callable with anything.
    return null;
  }

  const start = parsed.searchParams.get("start");
  if (start) {
    return resolveStartParam(start);
  }

  return isKnownAppRoute(pathname) ? pathname : null;
}
